import {navigationItemHasVisibleSidebarChildren} from '@rdcomponents/app/routing/helpers/navigation-sidebar-menu-children';
import {
    findItemByPath,
    getFirstPagePath,
    getItemsWithWeights,
} from '@rdcomponents/navigation/favorite-routes/favorites-helper';
import {ReactNode, useMemo} from 'react';

import {useNavigationConfig} from '../../app/routing/hooks/use-app-navigation';

export type SearchableMenuItemType = {
    path: string;
    displayLabel: string;
    parentLabel?: string;
    icon?: ReactNode;
    navigatePath: string;
    /**
     * Same handler as the sidebar menu item `onClick` (Multichat, system status, …). When set, quick search
     * runs this instead of in-app route navigation.
     */
    onSelect?: () => void;
};

export function useSearchableMenuItems(): Array<SearchableMenuItemType> {
    const {navigationConfig} = useNavigationConfig();

    // eslint-disable-next-line sonarjs/cognitive-complexity
    return useMemo(() => {
        const itemsWithWeights = getItemsWithWeights(navigationConfig);

        return (
            itemsWithWeights
                // eslint-disable-next-line complexity
                .flatMap((entry) => {
                    const {item, path, parentPath} = entry;
                    const hasMenuChildren = navigationItemHasVisibleSidebarChildren(item);
                    const routeKey = (item.path ?? item.key)?.toString();

                    if (!routeKey || hasMenuChildren || !item.label) {
                        return [];
                    }

                    if (item.ignoreForSearch) {
                        return [];
                    }

                    // Key-only custom rows (no path) are included only with `onClick`, otherwise the search has nothing
                    // valid to do on activate (e.g. external links, SSO).
                    if (!item.path && !item.onClick) {
                        return [];
                    }

                    const parentNavigationElement = parentPath ? findItemByPath(navigationConfig, parentPath) : null;
                    const parentLabel = parentNavigationElement ? parentNavigationElement.item.label : '';
                    const displayLabel = `${parentLabel ? parentLabel + ' > ' : ''}${item.label}`;

                    const navigatePath = getFirstPagePath(item) ?? path;
                    const icon = item.icon || parentNavigationElement?.item?.icon;
                    const menuOnClick = item.onClick;

                    const result: SearchableMenuItemType = {
                        path,
                        displayLabel,
                        navigatePath,
                        ...(parentLabel ? {parentLabel} : {}),
                        ...(icon ? {icon} : {}),
                        ...(menuOnClick ? {onSelect: menuOnClick} : {}),
                    };

                    return [result];
                })
        );
    }, [navigationConfig]);
}
